import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  BulkCategoryIdsPayload,
  BulkCategoryStatusPayload,
  Category,
  CategoryListParams,
  CreateCategoryPayload,
  PaginatedCategories,
  ReorderPayload,
  UpdateCategoryPayload,
} from '@/types/category';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildCategoryListQuery(params: CategoryListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const categoriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCategories: builder.query<PaginatedCategories, CategoryListParams>({
      query: (params) => ({
        url: `/categories?${buildCategoryListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildCategoryListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildCategoryListQuery(currentArg ?? {}) !== buildCategoryListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Categories' as const, id })),
              { type: 'Categories', id: 'LIST' },
            ]
          : [{ type: 'Categories', id: 'LIST' }],
    }),
    getCategory: builder.query<Category, string>({
      query: (id) => `/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Categories', id }],
    }),
    createCategory: builder.mutation<Category, CreateCategoryPayload>({
      query: (body) => ({ url: '/categories/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Categories', id: 'LIST' }],
    }),
    updateCategory: builder.mutation<Category, { id: string; body: UpdateCategoryPayload }>({
      query: ({ id, body }) => ({ url: `/categories/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Categories', id },
        { type: 'Categories', id: 'LIST' },
      ],
    }),
    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({ url: `/categories/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Categories', id: 'LIST' }],
    }),
    reorderCategories: builder.mutation<Category[], ReorderPayload>({
      query: (body) => ({ url: '/categories/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Categories', id: 'LIST' }],
    }),
    bulkUpdateCategoryStatus: builder.mutation<{ count: number }, BulkCategoryStatusPayload>({
      query: (body) => ({ url: '/categories/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Categories', id: 'LIST' }],
    }),
    bulkDeleteCategories: builder.mutation<{ count: number }, BulkCategoryIdsPayload>({
      query: (body) => ({ url: '/categories/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Categories', id: 'LIST' }],
    }),
    uploadCategoryImage: builder.mutation<{ image: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/categories/upload-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ image: string }>(json) ? json.data : (json as { image: string });
          const message = isApiResponse<{ image: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useReorderCategoriesMutation,
  useBulkUpdateCategoryStatusMutation,
  useBulkDeleteCategoriesMutation,
  useUploadCategoryImageMutation,
} = categoriesApi;
